import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../store';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  PiXLight,
  PiCheckCircleLight,
  PiWarningLight,
  PiQuestionLight,
  PiFileTextLight,
  PiColumnsLight,
  PiQuotesLight,
  PiTargetLight,
  PiShieldCheckLight,
  PiSpinnerGapLight,
  PiChatTextLight,
  PiPaperPlaneRightLight,
  PiPencilSimpleLight,
  PiTrashLight,
  PiAtLight,
} from 'react-icons/pi';
import { TableCell, TableColumn, TableRow, TableSharedUser, CellComment } from '../../types/batchTables';
import {
  fetchCellComments,
  addCellComment,
  updateCellComment,
  deleteCellComment,
} from '../../store/actions/BatchTables/BatchTablesActions';
import Button from '../shared/TailwindComponents/Button';
import ProfileImage from '../Header/ProfileImage';
import { useFeatureTour } from '../../hooks/useFeatureTour';
import { FeatureTour, cellDetailTourSteps } from '../shared/FeatureTour';

interface CellDetailPanelProps {
  cell: TableCell | null;
  column: TableColumn;
  row: TableRow;
  tableUuid: string;
  onClose: () => void;
}

const confidenceConfig: Record<string, { label: string; color: string; bg: string; border: string; icon: React.ReactNode }> = {
  high: {
    label: 'High',
    color: 'text-green-700 dark:text-green-400',
    bg: 'bg-green-50 dark:bg-green-900/20',
    border: 'border-green-200 dark:border-green-800',
    icon: <PiCheckCircleLight className="w-4 h-4 text-green-500" />,
  },
  medium: {
    label: 'Medium',
    color: 'text-yellow-700 dark:text-yellow-400',
    bg: 'bg-yellow-50 dark:bg-yellow-900/20',
    border: 'border-yellow-200 dark:border-yellow-800',
    icon: <PiShieldCheckLight className="w-4 h-4 text-yellow-500" />,
  },
  low: {
    label: 'Low',
    color: 'text-red-700 dark:text-red-400',
    bg: 'bg-red-50 dark:bg-red-900/20',
    border: 'border-red-200 dark:border-red-800',
    icon: <PiWarningLight className="w-4 h-4 text-red-500" />,
  },
  not_found: {
    label: 'Not found',
    color: 'text-gray-500 dark:text-gray-400',
    bg: 'bg-gray-50 dark:bg-gray-800',
    border: 'border-gray-200 dark:border-gray-700',
    icon: <PiQuestionLight className="w-4 h-4 text-gray-400" />,
  },
};

const CellDetailPanel: React.FC<CellDetailPanelProps> = ({ cell, column, row, tableUuid, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const conf = cell?.confidence ? confidenceConfig[cell.confidence] || confidenceConfig.not_found : null;

  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'cell-detail',
    steps: cellDetailTourSteps,
    readyToShow: !!cell,
    autoStartDelay: 600,
  });

  // User identity for ownership checks
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const currentUserUuid = userInfo?.UserUID || userInfo?.uuid_encrypted || '';

  // Collaborators for @mention — use has_access (owner + collaborators merged) with shared_with fallback
  const currentTable = useSelector((state: RootState) => state.batchTables.currentTable);
  const currentUserEmail = (userInfo?.Email || '').toLowerCase();
  const mentionableUsers = useMemo<TableSharedUser[]>(() => {
    // Prefer has_access (merged owner + collaborators), fall back to shared_with + owner
    let allUsers: TableSharedUser[] = [];
    if (currentTable?.has_access && currentTable.has_access.length > 0) {
      allUsers = currentTable.has_access;
    } else {
      if (currentTable?.owner) {
        allUsers.push({
          user_id: currentTable.owner.user_id,
          name: currentTable.owner.name,
          email: currentTable.owner.email,
          initials: currentTable.owner.initials,
        });
      }
      if (currentTable?.shared_with) {
        const ownerIdStr = currentTable.owner ? String(currentTable.owner.user_id) : null;
        for (const u of currentTable.shared_with) {
          if (ownerIdStr && String(u.user_id) === ownerIdStr) continue;
          allUsers.push(u);
        }
      }
    }
    // Filter out self by email (most reliable cross-identifier match)
    return allUsers.filter(
      (u) => u.email.toLowerCase() !== currentUserEmail
    );
  }, [currentTable, currentUserEmail]);

  // Cell comments state from Redux
  const cellComments = useSelector((state: RootState) => state.batchTables.cellComments);
  const isLoadingComments = useSelector((state: RootState) => state.batchTables.isLoadingComments);

  // Local state for comment input
  const [commentText, setCommentText] = useState('');
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [editingText, setEditingText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const commentInputRef = useRef<HTMLTextAreaElement>(null);
  const commentsEndRef = useRef<HTMLDivElement>(null);

  // @mention state
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [mentionIndex, setMentionIndex] = useState(0);
  const [mentionTarget, setMentionTarget] = useState<'new' | 'edit'>('new');
  const mentionDropdownRef = useRef<HTMLDivElement>(null);

  const filteredMentionUsers = useMemo(() => {
    if (!mentionSearch) return mentionableUsers;
    const q = mentionSearch.toLowerCase();
    return mentionableUsers.filter(
      (u) => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q)
    );
  }, [mentionableUsers, mentionSearch]);

  // Load comments when cell changes
  useEffect(() => {
    if (cell?.id && tableUuid) {
      dispatch(fetchCellComments(tableUuid, cell.id) as any);
    }
  }, [dispatch, tableUuid, cell?.id]);

  // Scroll to bottom when new comment added
  useEffect(() => {
    commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [cellComments.length]);

  // Close mention dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (mentionDropdownRef.current && !mentionDropdownRef.current.contains(e.target as Node)) {
        setShowMentionDropdown(false);
      }
    };
    if (showMentionDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showMentionDropdown]);

  // ─── Mention helpers ────────────────────────────────────────────────

  /** Find the @query being typed at cursor position */
  const getMentionQuery = (text: string, cursorPos: number): string | null => {
    const beforeCursor = text.slice(0, cursorPos);
    const match = beforeCursor.match(/@([^\s@\[\]]*)$/);
    return match ? match[1] : null;
  };

  /** Insert a mention at the current @ position */
  const insertMention = (user: TableSharedUser, target: 'new' | 'edit') => {
    const isNew = target === 'new';
    const text = isNew ? commentText : editingText;
    const ref = isNew ? commentInputRef.current : null;
    const cursorPos = ref?.selectionStart ?? text.length;

    const beforeCursor = text.slice(0, cursorPos);
    const atIndex = beforeCursor.lastIndexOf('@');
    if (atIndex === -1) return;

    const mentionTag = `@[${user.name}] `;
    const newText = text.slice(0, atIndex) + mentionTag + text.slice(cursorPos);

    if (isNew) {
      setCommentText(newText);
      // Move cursor after the mention
      setTimeout(() => {
        if (commentInputRef.current) {
          const newPos = atIndex + mentionTag.length;
          commentInputRef.current.selectionStart = newPos;
          commentInputRef.current.selectionEnd = newPos;
          commentInputRef.current.focus();
        }
      }, 0);
    } else {
      setEditingText(newText);
    }

    setShowMentionDropdown(false);
    setMentionSearch('');
    setMentionIndex(0);
  };

  /** Handle textarea change with @ detection */
  const handleCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setCommentText(val);

    const cursorPos = e.target.selectionStart;
    const query = getMentionQuery(val, cursorPos);

    if (query !== null && mentionableUsers.length > 0) {
      setMentionSearch(query);
      setMentionTarget('new');
      setShowMentionDropdown(true);
      setMentionIndex(0);
    } else {
      setShowMentionDropdown(false);
    }
  };

  /** Handle edit textarea change with @ detection */
  const handleEditCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setEditingText(val);

    const cursorPos = e.target.selectionStart;
    const query = getMentionQuery(val, cursorPos);

    if (query !== null && mentionableUsers.length > 0) {
      setMentionSearch(query);
      setMentionTarget('edit');
      setShowMentionDropdown(true);
      setMentionIndex(0);
    } else {
      setShowMentionDropdown(false);
    }
  };

  /** Trigger @ menu manually via button */
  const handleAtButtonClick = () => {
    if (!commentInputRef.current) return;
    const ref = commentInputRef.current;
    const cursorPos = ref.selectionStart;
    const newText = commentText.slice(0, cursorPos) + '@' + commentText.slice(cursorPos);
    setCommentText(newText);
    setMentionSearch('');
    setMentionTarget('new');
    setShowMentionDropdown(true);
    setMentionIndex(0);
    setTimeout(() => {
      ref.selectionStart = cursorPos + 1;
      ref.selectionEnd = cursorPos + 1;
      ref.focus();
    }, 0);
  };

  // ─── Comment CRUD ───────────────────────────────────────────────────

  const handleAddComment = useCallback(async () => {
    if (!commentText.trim() || !cell?.id || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await dispatch(addCellComment(tableUuid, cell.id, commentText.trim()) as any);
      setCommentText('');
    } finally {
      setIsSubmitting(false);
    }
  }, [dispatch, tableUuid, cell?.id, commentText, isSubmitting]);

  const handleUpdateComment = useCallback(async (commentId: number) => {
    if (!editingText.trim() || !cell?.id) return;
    await dispatch(updateCellComment(tableUuid, cell.id, commentId, editingText.trim()) as any);
    setEditingCommentId(null);
    setEditingText('');
  }, [dispatch, tableUuid, cell?.id, editingText]);

  const handleDeleteComment = useCallback(async (commentId: number) => {
    if (!cell?.id) return;
    await dispatch(deleteCellComment(tableUuid, cell.id, commentId) as any);
  }, [dispatch, tableUuid, cell?.id]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Mention dropdown keyboard navigation
    if (showMentionDropdown && filteredMentionUsers.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionIndex((prev) => (prev + 1) % filteredMentionUsers.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionIndex((prev) => (prev - 1 + filteredMentionUsers.length) % filteredMentionUsers.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        insertMention(filteredMentionUsers[mentionIndex], mentionTarget);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowMentionDropdown(false);
        return;
      }
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAddComment();
    }
  }, [handleAddComment, showMentionDropdown, filteredMentionUsers, mentionIndex, mentionTarget]);

  const handleEditKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>, commentId: number) => {
    // Mention dropdown keyboard navigation
    if (showMentionDropdown && filteredMentionUsers.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionIndex((prev) => (prev + 1) % filteredMentionUsers.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionIndex((prev) => (prev - 1 + filteredMentionUsers.length) % filteredMentionUsers.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        insertMention(filteredMentionUsers[mentionIndex], mentionTarget);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowMentionDropdown(false);
        return;
      }
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleUpdateComment(commentId);
    }
    if (e.key === 'Escape') {
      setEditingCommentId(null);
      setEditingText('');
    }
  }, [handleUpdateComment, showMentionDropdown, filteredMentionUsers, mentionIndex, mentionTarget]);

  const formatCommentTime = (dateStr: string | null) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return t('just now');
    if (diffMin < 60) return `${diffMin}m`;
    const diffHours = Math.floor(diffMin / 60);
    if (diffHours < 24) return `${diffHours}h`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d`;
    return d.toLocaleDateString();
  };

  /** Render highlighted overlay for textareas — shows @mentions as blue text.
   *  The overlay has the EXACT same characters as the textarea value so they
   *  stay pixel-aligned. The @[ and ] around mentions are rendered transparent
   *  while the name itself is rendered in blue. Non-mention text uses the
   *  normal text color. The textarea text is fully transparent. */
  const renderInputHighlight = (text: string): React.ReactNode => {
    if (!text) return '\u00A0';
    const mentionRegex = /@\[([^\]]+)\]/g;
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = mentionRegex.exec(text)) !== null) {
      // Normal text before the mention
      if (match.index > lastIndex) {
        parts.push(text.slice(lastIndex, match.index));
      }
      // @ and [ — keep as invisible characters for alignment
      parts.push(<span key={`pre-${match.index}`} className="text-transparent">@[</span>);
      // The name — visible in blue
      parts.push(
        <span key={`hl-${match.index}`} className="text-blue-600 dark:text-blue-400 font-medium">
          {match[1]}
        </span>
      );
      // ] — invisible for alignment
      parts.push(<span key={`post-${match.index}`} className="text-transparent">]</span>);
      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }

    parts.push('\u00A0');
    return parts;
  };

  /** Render comment text with @[Name] mentions highlighted */
  const renderCommentText = (text: string): React.ReactNode => {
    // Split on @[Name] pattern
    const mentionRegex = /@\[([^\]]+)\]/g;
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = mentionRegex.exec(text)) !== null) {
      // Text before the mention
      if (match.index > lastIndex) {
        parts.push(text.slice(lastIndex, match.index));
      }
      // The mention — clean inline blue text
      const mentionName = match[1];
      parts.push(
        <span
          key={`mention-${match.index}`}
          className="text-blue-600 dark:text-blue-400 font-medium"
        >
          @{mentionName}
        </span>
      );
      lastIndex = match.index + match[0].length;
    }

    // Remaining text after last mention
    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }

    return parts.length > 0 ? parts : text;
  };

  /** Mention dropdown component (shared between new & edit) */
  const MentionDropdown = () => {
    if (!showMentionDropdown) return null;
    return (
      <div
        ref={mentionDropdownRef}
        className="absolute bottom-full left-0 right-0 mb-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 max-h-[200px] overflow-y-auto"
      >
        <div className="px-2.5 py-1.5 border-b border-gray-100 dark:border-gray-700">
          <span className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider">
            {t('Mention someone')}
          </span>
        </div>
        {filteredMentionUsers.length === 0 ? (
          <div className="px-3 py-3 text-center">
            <p className="text-[11px] text-gray-400 dark:text-gray-500">
              {mentionableUsers.length === 0
                ? t('No collaborators on this table yet')
                : t('No matching collaborators')
              }
            </p>
          </div>
        ) : (
          filteredMentionUsers.map((user, idx) => (
            <button
              key={String(user.user_id)}
              type="button"
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors ${
                idx === mentionIndex
                  ? 'bg-blue-50 dark:bg-blue-900/20'
                  : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
              }`}
              onMouseDown={(e) => {
                e.preventDefault(); // prevent textarea blur
                insertMention(user, mentionTarget);
              }}
              onMouseEnter={() => setMentionIndex(idx)}
            >
              <ProfileImage
                profilePicture={user.profile_picture || null}
                email={user.email || null}
                initials={user.initials || user.name?.charAt(0) || '?'}
                name={user.name}
                size="w-6 h-6"
              />
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium text-gray-800 dark:text-gray-200 truncate">{user.name}</p>
                <p className="text-[10px] text-gray-400 dark:text-gray-500 truncate">{user.email}</p>
              </div>
            </button>
          ))
        )}
      </div>
    );
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="fixed inset-y-0 right-0 z-50 w-full max-w-lg bg-white dark:bg-gray-900 shadow-2xl border-l border-gray-200 dark:border-gray-700 flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 dark:border-gray-800 flex-shrink-0 bg-white dark:bg-gray-900">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
              <PiTargetLight className="h-4 w-4 text-gray-500 dark:text-gray-400" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                {t('Cell Detail')}
              </h3>
              <p className="text-[10px] text-gray-400 dark:text-gray-500 leading-none mt-0.5">
                {column.name}
              </p>
            </div>
          </div>
          <Button variant="neutral" size="small" onClick={onClose}>
            {t('Close')}
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4 bg-[#f5f5f5] dark:bg-gray-900">
          {/* Document + Column info — compact row */}
          <div data-tour-id="cell-detail-info" className="grid grid-cols-2 gap-3">
            <div className="p-3 rounded-lg bg-white dark:bg-gray-800/50 border border-gray-200 dark:border-gray-800">
              <div className="flex items-center gap-1.5 mb-1">
                <PiFileTextLight className="h-3.5 w-3.5 text-gray-400" />
                <span className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider">{t('Document')}</span>
              </div>
              <p className="text-xs font-medium text-gray-800 dark:text-gray-200 truncate">{row.file_name}</p>
            </div>
            <div className="p-3 rounded-lg bg-white dark:bg-gray-800/50 border border-gray-200 dark:border-gray-800">
              <div className="flex items-center gap-1.5 mb-1">
                <PiColumnsLight className="h-3.5 w-3.5 text-gray-400" />
                <span className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider">{t('Column')}</span>
              </div>
              <p className="text-xs font-medium text-gray-800 dark:text-gray-200 truncate">{column.name}</p>
            </div>
          </div>

          {/* Query */}
          {column.query && (
            <div className="p-3 rounded-lg bg-white dark:bg-gray-800/50 border border-gray-200 dark:border-gray-800">
              <span className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider">{t('Query')}</span>
              <p className="text-xs text-gray-600 dark:text-gray-400 italic mt-1 leading-relaxed">{column.query}</p>
            </div>
          )}

          {/* Empty / Pending state */}
          <div data-tour-id="cell-detail-answer">
          {(!cell || cell.status === 'pending') ? (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
              className="rounded-lg bg-white dark:bg-gray-800/50 border border-gray-200 dark:border-gray-800 p-8 text-center"
            >
              {row.status === 'processing' ? (
                <>
                  <PiSpinnerGapLight className="w-8 h-8 text-blue-400 mx-auto mb-3 animate-spin" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {t('Processing this cell…')}
                  </p>
                </>
              ) : (
                <>
                  <PiQuestionLight className="w-8 h-8 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {t('No answer yet. Run processing to extract this value.')}
                  </p>
                </>
              )}
            </motion.div>

          ) : cell.status === 'error' ? (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-lg bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 p-4"
            >
              <div className="flex items-center gap-2">
                <PiWarningLight className="w-4 h-4 text-red-500" />
                <p className="text-sm font-medium text-red-700 dark:text-red-400">
                  {t('Error extracting this value')}
                </p>
              </div>
            </motion.div>

          ) : (
            <>
              {/* Extracted Answer */}
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, delay: 0.05 }}
              >
                <div className="flex items-center gap-1.5 mb-2">
                  <PiTargetLight className="h-3.5 w-3.5 text-gray-400" />
                  <span className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider">{t('Answer')}</span>
                </div>
                <div className="p-4 rounded-lg bg-white dark:bg-gray-800/50 border border-gray-200 dark:border-gray-800">
                  <p className="text-sm whitespace-pre-wrap text-gray-800 dark:text-gray-200 leading-relaxed">
                    {cell.value || '—'}
                  </p>
                </div>
              </motion.div>

              {/* Source Excerpt */}
              {cell.source_excerpt && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2, delay: 0.15 }}
                >
                  <div className="flex items-center gap-1.5 mb-2">
                    <PiQuotesLight className="h-3.5 w-3.5 text-gray-400" />
                    <span className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider">{t('Source Excerpt')}</span>
                  </div>
                  <div className="p-4 rounded-lg bg-white dark:bg-gray-800/50 border border-gray-200 dark:border-gray-800 border-l-4 border-l-gray-300 dark:border-l-gray-600">
                    <p className="whitespace-pre-wrap text-xs italic text-gray-600 dark:text-gray-400 leading-relaxed">
                      &ldquo;{cell.source_excerpt}&rdquo;
                    </p>
                  </div>
                </motion.div>
              )}
            </>
          )}
          </div>

          {/* ── Comments Section ── */}
          <motion.div
            data-tour-id="cell-detail-comments"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, delay: 0.2 }}
          >
            <div className="flex items-center gap-1.5 mb-3">
              <PiChatTextLight className="h-3.5 w-3.5 text-gray-400" />
              <span className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                {t('Comments')}
              </span>
              {cellComments.length > 0 && (
                <span className="text-[10px] text-gray-400 dark:text-gray-500 ml-1">
                  ({cellComments.length})
                </span>
              )}
            </div>

            {/* Comment List */}
            <div className="space-y-3 mb-3">
              {isLoadingComments ? (
                <div className="flex items-center justify-center py-4">
                  <PiSpinnerGapLight className="w-4 h-4 animate-spin text-gray-400" />
                </div>
              ) : cellComments.length === 0 ? (
                <p className="text-xs text-gray-400 dark:text-gray-500 text-center py-3">
                  {t('No comments yet. Be the first to add one.')}
                </p>
              ) : (
                cellComments.map((comment: CellComment) => {
                  const isOwn = comment.user_uuid === currentUserUuid;
                  const isEditing = editingCommentId === comment.id;

                  return (
                    <div
                      key={comment.id}
                      className="group rounded-lg bg-white dark:bg-gray-800/50 border border-gray-200 dark:border-gray-800 p-3"
                    >
                      {/* Comment header */}
                      <div className="flex items-center gap-2 mb-1.5">
                        {/* Avatar */}
                        <ProfileImage
                          profilePicture={comment.user_avatar_url || null}
                          email={comment.user_email || null}
                          initials={comment.user_initials || comment.user_name?.charAt(0) || '?'}
                          name={comment.user_name}
                          size="w-5 h-5"
                          className="text-[8px]"
                        />
                        <span className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate">
                          {comment.user_name}
                        </span>
                        <span className="text-[10px] text-gray-400 dark:text-gray-500 flex-shrink-0">
                          {formatCommentTime(comment.created_at)}
                        </span>
                        {comment.is_edited && (
                          <span className="text-[9px] text-gray-400 dark:text-gray-600 italic">
                            ({t('edited')})
                          </span>
                        )}

                        {/* Own comment actions */}
                        {isOwn && !isEditing && (
                          <div className="ml-auto flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => {
                                setEditingCommentId(comment.id);
                                setEditingText(comment.comment_text);
                              }}
                              className="p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                              title={t('Edit')}
                            >
                              <PiPencilSimpleLight className="w-3 h-3 text-gray-400" />
                            </button>
                            <button
                              onClick={() => handleDeleteComment(comment.id)}
                              className="p-0.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                              title={t('Delete')}
                            >
                              <PiTrashLight className="w-3 h-3 text-red-400" />
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Comment body */}
                      {isEditing ? (
                        <div className="mt-1 relative">
                          <div className="relative">
                            {/* Highlight overlay for edit textarea */}
                            {editingText.includes('@[') && (
                              <div
                                aria-hidden
                                className="absolute inset-0 px-2.5 py-1.5 text-xs leading-normal whitespace-pre-wrap break-words pointer-events-none overflow-hidden text-gray-800 dark:text-gray-200"
                                style={{ wordBreak: 'break-word' }}
                              >
                                {renderInputHighlight(editingText)}
                              </div>
                            )}
                            <textarea
                              value={editingText}
                              onChange={handleEditCommentChange}
                              onKeyDown={(e) => handleEditKeyDown(e, comment.id)}
                              rows={2}
                              className="w-full text-xs bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-md px-2.5 py-1.5 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:focus:ring-primary-700 resize-none relative z-[1] caret-gray-800 dark:caret-gray-200"
                              style={{ color: editingText.includes('@[') ? 'transparent' : undefined }}
                              autoFocus
                            />
                          </div>
                          {showMentionDropdown && mentionTarget === 'edit' && <MentionDropdown />}
                          <div className="flex items-center gap-2 mt-1.5">
                            <Button
                              variant="primary"
                              size="small"
                              onClick={() => handleUpdateComment(comment.id)}
                              disabled={!editingText.trim()}
                            >
                              {t('Save')}
                            </Button>
                            <Button
                              variant="neutral"
                              size="small"
                              onClick={() => { setEditingCommentId(null); setEditingText(''); }}
                            >
                              {t('Cancel')}
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <p className="text-xs text-gray-600 dark:text-gray-400 whitespace-pre-wrap leading-relaxed">
                          {renderCommentText(comment.comment_text)}
                        </p>
                      )}
                    </div>
                  );
                })
              )}
              <div ref={commentsEndRef} />
            </div>

            {/* New Comment Input */}
            {cell && (
              <div className="relative">
                {/* Mention dropdown rendered outside the bordered box so it isn't clipped */}
                {showMentionDropdown && mentionTarget === 'new' && <MentionDropdown />}
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50">
                  <div className="relative">
                    {/* Highlight overlay — only shown when text contains mentions */}
                    {commentText.includes('@[') && (
                      <div
                        aria-hidden
                        className="absolute inset-0 px-3 py-2.5 text-xs leading-normal whitespace-pre-wrap break-words pointer-events-none overflow-hidden text-gray-800 dark:text-gray-200"
                        style={{ wordBreak: 'break-word' }}
                      >
                        {renderInputHighlight(commentText)}
                      </div>
                    )}
                    <textarea
                      ref={commentInputRef}
                      value={commentText}
                      onChange={handleCommentChange}
                      onKeyDown={handleKeyDown}
                      placeholder={t('Write a comment… Use @ to mention someone')}
                      rows={2}
                      className="w-full text-xs bg-transparent px-3 py-2.5 text-gray-800 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none resize-none rounded-t-lg relative z-[1] caret-gray-800 dark:caret-gray-200"
                      style={{ color: commentText.includes('@[') ? 'transparent' : undefined }}
                    />
                  </div>
                  <div className="flex items-center justify-between px-2 py-1.5 border-t border-gray-100 dark:border-gray-700/50">
                    <button
                      type="button"
                      onClick={handleAtButtonClick}
                      className="p-1.5 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-gray-400 hover:text-blue-500 dark:hover:text-blue-400"
                      title={t('Mention someone')}
                    >
                      <PiAtLight className="w-4 h-4" />
                    </button>
                    <Button
                      variant="primary"
                      size="small"
                      onClick={handleAddComment}
                      disabled={!commentText.trim() || isSubmitting}
                      isLoading={isSubmitting}
                      icon={<PiPaperPlaneRightLight className="w-3.5 h-3.5" />}
                    >
                      {t('Send')}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </motion.div>
      <FeatureTour
        steps={cellDetailTourSteps}
        run={isTourRunning}
        onCallback={handleTourCallback}
        continuous
        showProgress
        showSkipButton
        disableOverlay
      />
    </AnimatePresence>
  );
};

export default CellDetailPanel;